import type { GuidanceSource } from '@bookora/shared';

import { useSessionStore } from '@/auth/token-store';
import { toast } from '@/lib/feedback';

import { tourAllowed } from './resolve-steps';
import { armedToast } from './runtime-copy';
import { CONTEXT_ARMED_TTL_MS } from './timing';
import { selectBusy, useTourStore } from './tour-store';
import { COPY } from './tours/copy';
import { TOURS } from './tours/registry';
import { NO_TOUR_FEATURES, type TourFeature, type TourKey } from './tours/types';

/**
 * The imperative half of the public API: callable from anywhere (a hub row, a
 * smart-guide card, a deep link), outside React. `GuidanceRoot` hands this
 * module the router's `navigate` and the viewer's feature gates.
 */

type Navigate = (to: string) => void;

let navigateImpl: Navigate | null = null;
let features: Record<TourFeature, boolean> = { ...NO_TOUR_FEATURES };

/** @internal `GuidanceRoot` */
export function setGuidanceNavigator(navigate: Navigate | null): void {
  navigateImpl = navigate;
}

/** @internal `GuidanceRoot` */
export function setGuidanceFeatures(next: Record<TourFeature, boolean>): void {
  features = next;
}

export function guidanceFeatures(): Record<TourFeature, boolean> {
  return features;
}

/** Opens the hub, optionally focused on a section and an item (e.g. a tip). */
export function openGuideHub(
  opts?: { section?: string; id?: string },
  source: GuidanceSource = 'help-menu',
): void {
  const focus = opts && (opts.section || opts.id) ? { section: opts.section, id: opts.id } : null;
  useTourStore.getState().openHub(focus, source);
}

export interface StartTourOptions {
  source?: GuidanceSource;
  startAtStepId?: string;
  /** Hub «ادامه»: resume at this step index (clamped). */
  resumeIndex?: number;
  /** Where focus returns when the tour ends (default: the launcher, else `#bk-main`). */
  returnFocusTo?: HTMLElement | null;
}

/**
 * Starts a tour, or ARMS it when it cannot start here:
 *
 *  · a shell tour starts anywhere;
 *  · a page tour on its route starts (its anchors are awaited ≤ 1.5 s);
 *    elsewhere the panel navigates there and the page's chip starts it once
 *    the page reports ready (armed ten minutes);
 *  · a context tour (a patient's file, an inline form, an import review)
 *    cannot be navigated to — it waits in this tab for a working day, a toast
 *    says where it will start, and the shell's armed notice keeps saying so
 *    (with «لغو») until it does.
 *
 * From inside the hub, the hub closes first and the start runs after its
 * exit: the hub is a modal dialog and would block the tour otherwise.
 * A tour the viewer may not see is ignored silently.
 */
export function startTour(key: TourKey, opts: StartTourOptions = {}): void {
  const store = useTourStore.getState();
  if (store.hubOpen) {
    store.closeHub(() => startTourNow(key, opts));
    return;
  }
  startTourNow(key, opts);
}

function startTourNow(key: TourKey, opts: StartTourOptions): void {
  const tour = TOURS[key];
  if (!tour) return;
  const permissions = useSessionStore.getState().permissions;
  if (!tourAllowed(tour, permissions, features)) return;

  const source = opts.source ?? 'help-menu';
  const store = useTourStore.getState();
  const start = (): void =>
    store.start(key, {
      source,
      startAtStepId: opts.startAtStepId,
      resumeIndex: opts.resumeIndex,
      returnFocusTo: opts.returnFocusTo ?? null,
    });

  if (tour.surface === 'shell') {
    start();
    return;
  }

  if (tour.surface === 'inline') {
    if (store.inlineHost?.key === key && store.inlineHost.open) {
      start();
      return;
    }
    store.arm(key, source, opts.startAtStepId, CONTEXT_ARMED_TTL_MS, opts.resumeIndex);
    // Keyed by the tour's own host: the booking form must not say «ثبت جلسه».
    const host = tour.inlineHost;
    if (host) toast.info(armedToast(host));
    return;
  }

  const route = tour.route;
  if (!route) return;
  if (route.match(store.pathname, store.search)) {
    // A deep link lands while its page is still loading: arm, and the page's
    // chip starts it once the page reports ready.
    if (source === 'deeplink')
      store.arm(key, source, opts.startAtStepId, undefined, opts.resumeIndex);
    else start();
    return;
  }
  if (route.context) {
    store.arm(key, source, opts.startAtStepId, CONTEXT_ARMED_TTL_MS, opts.resumeIndex);
    toast.info(COPY.toast.armed[route.context]);
  } else {
    store.arm(key, source, opts.startAtStepId, undefined, opts.resumeIndex);
  }
  navigateImpl?.(route.path);
}

/** «لغو» on the armed notice: the waiting tour is dropped. */
export function cancelArmedTour(): void {
  useTourStore.getState().disarm();
}

/** «کمک برای همین صفحه»: the help-notes dialog for `section` (`''` = support only). */
export function openHelpNotes(section: string): void {
  useTourStore.getState().setHelpNotes(section);
}

/**
 * True while the welcome is unresolved in this tab, or a tour, an offer, the
 * hub or the welcome is open — and for the rest of a page visit in which a
 * tour offer appeared. Nothing proactive may appear while it is true.
 */
export function useGuidanceBusy(): boolean {
  return useTourStore(selectBusy);
}

/** A tip card or inline tip rendered in this page visit: no tour auto-offer follows it. */
export function notifyProactiveSurface(): void {
  useTourStore.getState().markProactiveSurface();
}
